import { useCallback, useEffect, useRef, useState } from 'react';

const read = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('gv_keno_settings')) || {};
    return { instant: !!saved.instant, turbo: !!saved.turbo && !saved.instant, muted: !!saved.muted, volume: Number.isFinite(saved.volume) ? Math.max(0, Math.min(100, saved.volume)) : 60 };
  } catch { return { instant: false, turbo: false, muted: false, volume: 60 }; }
};

export const useGamePreferences = (owner, auto, busy) => {
  const [applied, setApplied] = useState(read), [draft, setDraft] = useState(applied), [pending, setPending] = useState('');
  const actual = useRef(applied), desired = useRef(applied), queued = useRef(null), timer = useRef(null);
  const status = useRef({ auto, busy });
  status.current = { auto, busy };
  const commit = useCallback((value) => {
    actual.current = value; setApplied(value);
    localStorage.setItem('gv_keno_settings', JSON.stringify(value));
  }, []);
  const beginRound = useCallback(() => {
    if (queued.current?.ready) {
      commit(queued.current.value); queued.current = null; setPending('');
    }
    return actual.current;
  }, [commit]);
  const change = (field, value) => {
    const next = { ...desired.current, [field]: value };
    if (field === 'instant' && value) next.turbo = false;
    if (field === 'turbo' && value) next.instant = false;
    desired.current = next; setDraft(next); clearTimeout(timer.current);
    if (!status.current.auto) { queued.current = null; setPending(''); commit(next); return; }
    queued.current = { value: next, ready: false }; setPending('Applying in 1.5 seconds…');
    timer.current = setTimeout(() => {
      queued.current = { value: next, ready: true };
      // Sound is safe during a round; animation speed is sampled at admission.
      commit({ ...actual.current, muted: next.muted, volume: next.volume });
      if ((!status.current.busy && !status.current.auto) || (actual.current.instant === next.instant && actual.current.turbo === next.turbo)) beginRound();
      else setPending('Applying speed on the next bet…');
    }, 1500);
  };
  useEffect(() => { if (!auto && !busy && queued.current?.ready) beginRound(); }, [auto, busy, beginRound]);
  useEffect(() => {
    desired.current = actual.current; setDraft(actual.current); setPending('');
    return () => { clearTimeout(timer.current); queued.current = null; };
  }, [owner]);
  return { ...applied, beginRound, getPreferences: () => actual.current,
    setInstant: (value) => change('instant', value), setTurbo: (value) => change('turbo', value),
    setMuted: (value) => change('muted', value), setVolume: (value) => change('volume', value),
    settingsControls: { ...draft, pending } };
};